import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { cx, useUi } from "../../styles";
import ModalCard from "../../components/ModalCard";
import Plate from "../../components/Plate";
import { formatMoney } from "../../helpers";

const useStyles = makeStyles({
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12, fontSize: 14 },
  question: { fontSize: 15, fontWeight: 600, color: T.fg, margin: "4px 0 10px" },
  meta: { fontSize: 12, color: T.mutedFg },
  lines: { marginTop: 12, display: "flex", flexDirection: "column", gap: 6 },
  line: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, color: T.mutedFg },
  total: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14, fontWeight: 600, color: T.fg, borderTop: `1px solid ${T.borderSoft}`, paddingTop: 8, marginTop: 2 },
  staff: { border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, marginTop: 12 },
  notice: { borderRadius: 8, padding: "10px 12px", fontSize: 13, background: T.surface, color: T.mutedFg, marginTop: 10 },
  small: { padding: "8px 12px", fontSize: 13 },
});

/*
 * "Posted to room" - the desk confirming it put this chargeback on the guest's
 * folio. Confirmation is irreversible, which is why the figures are restated here in full.
 */
export default function ConfirmPostingModal({
  row,
  hotelName,
  staffName,
  staffRole,
  canConfirm,
  submitting,
  onConfirm,
  onClose,
}) {
  const s = useStyles();
  const ui = useUi();

  if (!row) return null;

  const { booking, currency } = row;

  const lines = [
    { label: "Parking", amount: row.parkingAud },
    { label: "Overstay", amount: row.overstayAud },
    { label: "Valet fees", amount: row.valetFeesAud },
  ].filter((line) => Number(line.amount) > 0);

  return (
    <ModalCard
      title="Posted to room"
      onClose={onClose}
      width={440}
      z={1450}
      footerLeft={
        <button type="button" className={cx(ui.mutedBtn, s.small)} onClick={onClose}>
          Cancel
        </button>
      }
      footerRight={
        canConfirm ? (
          <button
            type="button"
            className={cx(ui.primaryBtn, s.small)}
            disabled={submitting}
            onClick={onConfirm}
          >
            Confirm
          </button>
        ) : null
      }
    >
      <div className={s.head}>
        <Plate size={14}>{booking?.plate}</Plate>
        <strong>{booking?.guestName}</strong>
        {booking?.guestRoom ? <span className={s.meta}>Room {booking.guestRoom}</span> : null}
      </div>

      <div className={s.question}>Confirm this booking has been posted to the guest's room.</div>
      <div className={s.meta}>
        {formatMoney(row.totalAud, currency)} system-calculated{hotelName ? ` · ${hotelName}` : ""}.
        This records a room posting, not a card payment.
      </div>

      <div className={s.lines}>
        {lines.map((line) => (
          <div key={line.label} className={s.line}>
            <span>{line.label}</span>
            <span>{formatMoney(line.amount, currency)}</span>
          </div>
        ))}
        <div className={s.total}>
          <span>Total</span>
          <span>{formatMoney(row.totalAud, currency)}</span>
        </div>
      </div>

      {canConfirm ? (
        <div className={s.staff} aria-label="Confirming staff member">
          <div>
            Confirming as <strong>{staffName}</strong>
            {staffRole ? ` · ${staffRole}` : ""}
          </div>
          <div className={s.meta}>
            Recorded with the time and this booking. Signed-in Console identity; nothing to type.
            This cannot be undone.
          </div>
        </div>
      ) : (
        <div className={s.notice}>
          Confirming a posting needs the "Manage bookings" permission (Console → User Roles).
        </div>
      )}
    </ModalCard>
  );
}
